import { InfiniteData } from '@tanstack/query-core';
import { useMutation } from '@tanstack/react-query';
import { Program, ProgramsData } from '@shared/types/programs';
import { API } from '@src/api/index.ts';
import { ProgramsListParams, UpdateProgramPublicationDto } from '@src/api/programs.ts';
import { queryClient } from '@src/api/query-client.ts';

export const useUpdateProgramPublicationMutation = () => {
  return useMutation({
    mutationFn: async (params: UpdateProgramPublicationDto) =>
      await API.programs.updatePublication(params),
    onSuccess: (response, { id }) => {
      queryClient.setQueriesData(
        { queryKey: ['programsHistory'] },
        (oldData: InfiniteData<ProgramsData>) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              results: page.results.map((item) =>
                item.pk === id ? { ...item, publication: { ...response } } : item
              ),
            })),
          };
        }
      );

      queryClient.setQueriesData(
        { queryKey: ['programs'] },
        (oldData: InfiniteData<ProgramsData>) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              results: page.results.map((item) =>
                item.pk === id ? { ...item, publication: { ...response } } : item
              ),
            })),
          };
        }
      );

      queryClient.setQueriesData<InfiniteData<ProgramsData>>(
        {
          queryKey: ['programs'],
          predicate: (query) => {
            const [, params] = query.queryKey as ['programs', ProgramsListParams];

            return params.tabName === 'all';
          },
        },
        (oldData) => {
          if (!oldData) return oldData;
          if (!response.data.is_published) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                results: [...page.results.filter((item) => item.pk !== Number(id))],
              })),
            };
          } else {
            return oldData;
          }
        }
      );

      queryClient.setQueriesData({ queryKey: ['program', String(id)] }, (oldData: Program) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          publication: { ...oldData.publication, ...response.data },
        };
      });
      queryClient.invalidateQueries({ queryKey: ['program', String(id)] });
      queryClient.invalidateQueries({ queryKey: ['programs'] });
      queryClient.invalidateQueries({ queryKey: ['programsHistory'] });
    },
  });
};
